import { motion, AnimatePresence } from 'framer-motion';
import { ARENA, themeOf, rarityOf } from '../lib/classTheme';
import useTheme from '../lib/useTheme';

/* ============================================================
   GOLD FRAME — ornate roman border / neon holo border
   ============================================================ */
export function GoldFrame({ children, className = '', dark = false, glow, thick = false }) {
  const { isNeon } = useTheme();

  return (
    <div className={`relative ${className}`}
      style={{
        borderRadius: 'var(--radius)',
        padding: thick ? 3 : 2,
        background: ARENA.goldFrame,
        boxShadow: glow
          ? `0 0 26px -4px ${glow}`
          : isNeon
            ? '0 0 22px -6px rgba(0,229,255,.5)'
            : '0 4px 20px -8px rgba(133,96,31,.5)',
      }}>
      <div className="overflow-hidden h-full"
        style={{
          borderRadius: 'calc(var(--radius) - 2px)',
          background: dark ? ARENA.arenaDark : ARENA.marbleBg,
        }}>
        {children}
      </div>
      {!isNeon && <><Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" /></>}
      {isNeon && <><HoloCorner pos="tl" /><HoloCorner pos="br" /></>}
    </div>
  );
}

function Corner({ pos }) {
  const p = {
    tl: 'top-0 left-0', tr: 'top-0 right-0 rotate-90',
    bl: 'bottom-0 left-0 -rotate-90', br: 'bottom-0 right-0 rotate-180',
  }[pos];
  return (
    <svg className={`absolute ${p} w-4 h-4 pointer-events-none`} viewBox="0 0 16 16" fill="none">
      <path d="M0 6 Q0 0 6 0 L4 0 Q0 0 0 4 Z" fill={ARENA.goldL} opacity=".9" />
      <circle cx="3" cy="3" r="1.4" fill={ARENA.goldL} />
    </svg>
  );
}

function HoloCorner({ pos }) {
  const p = { tl: 'top-0 left-0', br: 'bottom-0 right-0 rotate-180' }[pos];
  return (
    <svg className={`absolute ${p} w-5 h-5 pointer-events-none`} viewBox="0 0 20 20" fill="none">
      <path d="M0 8 L0 0 L8 0" stroke={ARENA.goldL} strokeWidth="2" opacity=".9" />
      <circle cx="2.5" cy="2.5" r="1.6" fill={ARENA.goldL} />
    </svg>
  );
}

/* ============================================================
   ARENA HEADER
   ============================================================ */
export function ArenaHeader({ title, subtitle, icon, classKey, right, children, compact }) {
  const t = classKey ? themeOf(classKey) : null;
  const { isNeon } = useTheme();

  return (
    <div className="card-arena arena-arches overflow-hidden">
      {t && (
        <motion.div className="absolute inset-0"
          style={{ background: t.radial, opacity: isNeon ? 0.55 : 0.45 }}
          animate={{ opacity: isNeon ? [0.35, 0.6, 0.35] : [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }} />
      )}

      <Torch className="left-3 top-3" />
      <Torch className="right-3 top-3" delay={0.6} />
      <DustLayer count={isNeon ? 14 : 10} />

      <div className={`relative ${compact ? 'p-4' : 'p-5'}`}>
        <div className="flex items-start gap-3">
          {icon && (
            <div className="w-12 h-12 grid place-items-center shrink-0 text-2xl"
              style={{
                borderRadius: 'var(--radius)',
                background: t ? `${t.hex}28` : 'var(--accent-soft)',
                border: `1.5px solid ${t?.hex || ARENA.gold}`,
                boxShadow: `0 0 20px -4px ${t?.hex || ARENA.gold}`,
              }}>
              {icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-extrabold tracking-wide neon-glow-text"
              style={{
                color: ARENA.arenaText,
                textShadow: isNeon ? undefined : `0 2px 14px ${ARENA.gold}66`,
              }}>
              {title}
            </h1>
            {subtitle && (
              <p className="text-[11px] sm:text-xs font-semibold mt-0.5"
                style={{ color: ARENA.arenaTextSoft }}>
                {subtitle}
              </p>
            )}
          </div>
          {right}
        </div>
        {children}
      </div>

      <div className="h-[3px]" style={{ background: ARENA.goldFrame }} />
    </div>
  );
}

/* ============================================================
   TORCH / ENERGY EMITTER
   ============================================================ */
export function Torch({ className = '', delay = 0, size = 'md' }) {
  const { isNeon } = useTheme();
  const s = { sm: 'w-1.5 h-3', md: 'w-2 h-4', lg: 'w-3 h-6' }[size];

  if (isNeon) {
    return (
      <div className={`absolute pointer-events-none ${className}`}>
        <motion.div className={s.replace('h-', 'h-')}
          style={{
            width: size === 'lg' ? 3 : size === 'md' ? 2 : 1.5,
            height: size === 'lg' ? 26 : size === 'md' ? 18 : 12,
            background: 'linear-gradient(180deg,#fff,#00e5ff 40%,transparent)',
            boxShadow: '0 0 18px 6px rgba(0,229,255,.6), 0 0 40px 14px rgba(0,229,255,.2)',
            borderRadius: 2,
          }}
          animate={{ opacity: [.5, 1, .65, .95, .5], scaleY: [1, 1.15, .95, 1.08, 1] }}
          transition={{ duration: 2.6, repeat: Infinity, delay, ease: 'easeInOut' }} />
      </div>
    );
  }

  return (
    <div className={`absolute pointer-events-none ${className}`}>
      <motion.div className={`${s} rounded-full`}
        style={{
          background: 'linear-gradient(180deg, #fff3d6, #ff8c42 45%, #e5732b)',
          filter: 'blur(1px)',
          boxShadow: '0 0 22px 8px rgba(255,140,66,.55), 0 0 44px 16px rgba(255,140,66,.22)',
        }}
        animate={{ scaleY: [1, 1.25, .92, 1.12, 1], opacity: [.85, 1, .8, .95, .85] }}
        transition={{ duration: 2.2, repeat: Infinity, delay, ease: 'easeInOut' }} />
    </div>
  );
}

/* ============================================================
   DUST / DATA PARTICLES
   ============================================================ */
export function DustLayer({ count = 12, color }) {
  const { isNeon } = useTheme();
  const c = color || (isNeon ? 'rgba(0,229,255,.5)' : 'rgba(232,200,116,.4)');

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="dust-particle absolute"
          style={{
            left: `${(i * 8.3 + 4) % 96}%`,
            bottom: `${(i * 11) % 40}%`,
            width: isNeon ? 1.5 : 1 + (i % 3),
            height: isNeon ? 1.5 + (i % 4) * 2 : 1 + (i % 3),
            background: c,
            borderRadius: isNeon ? 1 : '50%',
            boxShadow: isNeon ? `0 0 6px ${c}` : 'none',
            animationDuration: `${5 + (i % 5) * 1.6}s`,
            animationDelay: `${i * 0.55}s`,
          }} />
      ))}
    </div>
  );
}

/* ============================================================
   CROWD / SPECTATOR ROW
   ============================================================ */
export function CrowdRow({ className = '', opacity = 0.22 }) {
  const { isNeon } = useTheme();

  if (isNeon) {
    return (
      <div className={`absolute inset-x-0 pointer-events-none ${className}`} style={{ opacity }}>
        <svg viewBox="0 0 400 20" className="w-full h-5" preserveAspectRatio="none">
          {Array.from({ length: 50 }).map((_, i) => (
            <motion.rect key={i}
              x={i * 8 + 2}
              width={4} height={10} rx={1}
              fill="#00e5ff"
              initial={{ y: 4, opacity: 0.4 }}
              animate={{ y: [4, 2, 4], opacity: [0.2, 0.8, 0.2] }}
              transition={{ duration: 1.4 + (i % 6) * 0.22, repeat: Infinity, delay: i * 0.05 }} />
          ))}
          <rect x="0" y="17" width="400" height="3" fill="#00e5ff" opacity=".4" />
        </svg>
      </div>
    );
  }

  return (
    <div className={`absolute inset-x-0 pointer-events-none ${className}`} style={{ opacity }}>
      <svg viewBox="0 0 400 24" className="w-full h-6" preserveAspectRatio="none">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.circle key={i}
            cx={i * 10 + 5}
            r={3.5 + (i % 3) * 0.6}
            fill="#241e16"
            initial={{ cy: 16 }}
            animate={{ cy: [16, 14.5, 16] }}
            transition={{ duration: 1.6 + (i % 5) * 0.28, repeat: Infinity, delay: i * 0.06 }} />
        ))}
        <rect x="0" y="19" width="400" height="5" fill="#241e16" />
      </svg>
    </div>
  );
}

/* ============================================================
   LAUREL / HEX EMBLEM
   ============================================================ */
export function Laurel({ size = 40, color, className = '' }) {
  const { isNeon } = useTheme();
  const c = color || ARENA.gold;

  if (isNeon) {
    return (
      <svg width={size} height={size * 0.62} viewBox="0 0 100 62"
        className={className} fill="none">
        <motion.path d="M50 6 L82 24 L82 44 L50 58 L18 44 L18 24 Z"
          stroke={c} strokeWidth="2.4" opacity=".9"
          animate={{ opacity: [.6, 1, .6] }}
          transition={{ duration: 2.4, repeat: Infinity }} />
        <path d="M50 16 L72 29 L72 41 L50 50 L28 41 L28 29 Z"
          stroke={c} strokeWidth="1.3" opacity=".45" />
        <circle cx="50" cy="32" r="3.5" fill={c} opacity=".9" />
        <path d="M18 24 L4 24 M82 24 L96 24" stroke={c} strokeWidth="1.6" opacity=".5" />
      </svg>
    );
  }

  return (
    <svg width={size} height={size * 0.62} viewBox="0 0 100 62"
      className={className} fill="none">
      {[0, 1].map(side => (
        <g key={side} transform={side ? 'scale(-1,1) translate(-100,0)' : ''}>
          <path d="M50 58 Q26 50 16 32 Q10 20 14 8"
            stroke={c} strokeWidth="2.2" strokeLinecap="round" opacity=".95" />
          {[0, 1, 2, 3, 4].map(i => (
            <ellipse key={i}
              cx={38 - i * 6} cy={48 - i * 9}
              rx="6.5" ry="3.4" fill={c}
              transform={`rotate(${-38 - i * 9} ${38 - i * 6} ${48 - i * 9})`}
              opacity={0.95 - i * 0.09} />
          ))}
        </g>
      ))}
    </svg>
  );
}

/* ============================================================
   SECTION TITLE
   ============================================================ */
export function ArenaTitle({ title, subtitle, latin, right, laurel = false, className = '' }) {
  const { isNeon } = useTheme();

  return (
    <div className={`flex items-end justify-between gap-3 mb-3 ${className}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {laurel && <Laurel size={26} color={ARENA.gold} />}
          <h2 className="font-display text-lg font-extrabold text-engraved tracking-wide"
            style={{ color: 'var(--text)' }}>
            {title}
          </h2>
        </div>
        {(subtitle || (latin && !isNeon)) && (
          <p className="text-[11px] font-semibold mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {latin && !isNeon && <span className="italic opacity-80">{latin}</span>}
            {latin && !isNeon && subtitle && ' · '}
            {subtitle}
          </p>
        )}
      </div>
      {right}
    </div>
  );
}

/* ============================================================
   PEDESTAL / HOLO PLATFORM
   ============================================================ */
export function Pedestal({ children, glow, className = '', width = '86%' }) {
  const { isNeon } = useTheme();

  return (
    <div className={`relative ${className}`}>
      <div className="relative z-10">{children}</div>

      {isNeon ? (
        <div className="relative mx-auto -mt-1" style={{ width }}>
          <motion.div className="h-[3px] rounded-full"
            style={{
              background: `linear-gradient(90deg, transparent, ${glow || ARENA.gold}, transparent)`,
              boxShadow: `0 0 14px 2px ${glow || ARENA.gold}`,
            }}
            animate={{ opacity: [.5, 1, .5] }}
            transition={{ duration: 2.2, repeat: Infinity }} />
          <div className="h-4 mx-auto rounded-b-lg"
            style={{
              width: '70%',
              background: `linear-gradient(180deg, ${glow || ARENA.gold}22, transparent)`,
            }} />
        </div>
      ) : (
        <div className="relative mx-auto -mt-1" style={{ width }}>
          <div className="h-2 rounded-t-sm"
            style={{ background: 'linear-gradient(180deg, var(--s-200), var(--s-300))' }} />
          <div className="h-3"
            style={{ background: 'linear-gradient(180deg, var(--s-300), var(--s-400))' }} />
          <div className="h-1.5 rounded-b-lg"
            style={{ background: 'linear-gradient(180deg, var(--s-500), var(--s-600))' }} />
        </div>
      )}

      <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 pointer-events-none"
        style={{
          width: '78%', height: 14,
          background: `radial-gradient(ellipse, ${glow ? glow + '55' : 'rgba(0,0,0,.3)'}, transparent 72%)`,
          filter: 'blur(5px)',
        }} />
    </div>
  );
}

/* ============================================================
   COLUMN / DATA PILLAR
   ============================================================ */
export function Column({ height = 120, className = '' }) {
  const { isNeon } = useTheme();

  if (isNeon) {
    return (
      <div className={`relative ${className}`} style={{ width: 6, height }}>
        <motion.div className="absolute inset-0 rounded-full"
          style={{
            background: `linear-gradient(180deg, ${ARENA.gold}, transparent)`,
            boxShadow: `0 0 12px 2px ${ARENA.gold}55`,
          }}
          animate={{ opacity: [.4, .9, .4] }}
          transition={{ duration: 2.8, repeat: Infinity }} />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ width: 22, height }}>
      <div className="absolute top-0 inset-x-0 h-3 rounded-t"
        style={{ background: 'linear-gradient(180deg, var(--s-100), var(--s-300))' }} />
      <div className="absolute top-3 inset-x-[2px] h-1.5" style={{ background: 'var(--s-400)' }} />
      <div className="absolute top-[18px] bottom-4 inset-x-[3px]"
        style={{ background: 'linear-gradient(90deg, var(--s-400), var(--s-100) 30%, var(--s-200) 60%, var(--s-500))' }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="absolute inset-y-0 w-px"
            style={{ left: `${25 + i * 25}%`, background: 'rgba(122,103,73,.35)' }} />
        ))}
      </div>
      <div className="absolute bottom-0 inset-x-0 h-4 rounded-b"
        style={{ background: 'linear-gradient(180deg, var(--s-300), var(--s-500))' }} />
    </div>
  );
}

/* ============================================================
   BANNER
   ============================================================ */
export function Banner({ children, color, className = '', width = 56 }) {
  const { isNeon } = useTheme();

  return (
    <div className={`relative ${className}`} style={{ width }}>
      <div className="h-1 rounded-t" style={{ background: ARENA.goldL }} />
      <div className="relative px-2 py-2 text-center"
        style={{
          background: color || (isNeon
            ? 'linear-gradient(135deg,#ff2e63,#a855f7)'
            : 'linear-gradient(135deg,#8b2635,#6d1e2a)'),
        }}>
        {children}
      </div>
      <svg width={width} height="12" className="block">
        <polygon points={`0,0 ${width},0 ${width / 2},12`}
          fill={isNeon ? '#a855f7' : '#6d1e2a'} opacity=".9" />
      </svg>
    </div>
  );
}

/* ============================================================
   STAT BAR
   ============================================================ */
export function ArenaStatBar({ value, max = 100, color, label, height = 8, showValue }) {
  const { isNeon } = useTheme();
  const pct = Math.max(0, Math.min(100, (value / max) * 100));

  return (
    <div className="relative">
      <div className="rounded-full overflow-hidden relative"
        style={{
          height,
          background: isNeon
            ? 'rgba(0,0,0,.5)'
            : 'linear-gradient(180deg, var(--s-300), var(--s-200))',
          boxShadow: isNeon
            ? 'inset 0 0 0 1px rgba(0,229,255,.15)'
            : 'inset 0 1px 3px rgba(0,0,0,.2)',
        }}>
        <motion.div className="h-full rounded-full relative"
          style={{
            background: color || ARENA.goldFrame,
            boxShadow: isNeon ? `0 0 10px ${color || ARENA.gold}88` : 'none',
          }}
          initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: .6, ease: 'easeOut' }}>
          {!isNeon && (
            <div className="absolute inset-0 rounded-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,.35), transparent 60%)' }} />
          )}
        </motion.div>
      </div>
      {showValue && (
        <span className="absolute inset-0 grid place-items-center text-[9px] font-black text-white"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,.6)' }}>
          {label || `${value}/${max}`}
        </span>
      )}
    </div>
  );
}

/* ============================================================
   TABS
   ============================================================ */
export function ArenaTabs({ tabs, value, onChange, className = '' }) {
  const { isNeon } = useTheme();

  return (
    <div className={`flex gap-1 p-1 overflow-x-auto no-scrollbar ${className}`}
      style={{
        borderRadius: 'var(--radius)',
        background: isNeon ? 'rgba(15,21,34,.7)' : 'linear-gradient(160deg, var(--s-100), var(--s-200))',
        border: '1px solid var(--border)',
        boxShadow: isNeon ? 'inset 0 0 0 1px rgba(0,229,255,.06)' : 'inset 0 1px 2px rgba(0,0,0,.06)',
      }}>
      {tabs.map(t => {
        const active = value === t.key;
        return (
          <button key={t.key} onClick={() => onChange(t.key)}
            className={`relative flex-1 min-w-[76px] flex items-center justify-center gap-1.5 py-2
              text-xs font-bold transition-all whitespace-nowrap
              ${active ? 'text-white' : ''}`}
            style={{
              borderRadius: 'var(--radius-sm)',
              color: active ? (isNeon ? '#04121a' : '#fff') : 'var(--text-muted)',
              background: active ? ARENA.goldFrame : 'transparent',
              boxShadow: active
                ? (isNeon ? '0 0 18px -4px rgba(0,229,255,.7)' : '0 2px 10px -3px rgba(201,151,58,.6)')
                : 'none',
              textShadow: active && !isNeon ? '0 1px 2px rgba(93,68,16,.4)' : 'none',
            }}>
            {t.icon && (typeof t.icon === 'string' ? <span>{t.icon}</span> : <t.icon size={13} />)}
            {t.label}
            {t.badge > 0 && (
              <span className="absolute top-0.5 right-1.5 min-w-[15px] h-[15px] px-1 rounded-full
                text-white text-[8px] font-black grid place-items-center"
                style={{ background: ARENA.blood }}>
                {t.badge > 9 ? '9+' : t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ============================================================
   RARITY BADGE
   ============================================================ */
export function RarityBadge({ rarity, size = 'sm', showLatin }) {
  const { isNeon } = useTheme();
  const r = rarityOf(rarity);
  const cls = size === 'lg' ? 'text-[11px] px-2.5 py-1' : 'text-[8px] px-1.5 py-0.5';

  return (
    <span className={`inline-block rounded-md font-black uppercase tracking-wider ${cls}`}
      style={{
        background: r.tier >= 5 ? ARENA.goldFrame : `${r.hex}22`,
        color: r.tier >= 5 ? (isNeon ? '#04121a' : '#fff') : r.hex,
        boxShadow: r.tier >= 4 ? r.shadowCss : 'none',
        border: isNeon && r.tier < 5 ? `1px solid ${r.hex}44` : 'none',
      }}>
      {showLatin && !isNeon ? r.latin : r.label}
    </span>
  );
}

/* ============================================================
   EMPTY STATE
   ============================================================ */
export function ArenaEmpty({ icon: Icon, title, sub, action, emoji }) {
  const { isNeon } = useTheme();

  return (
    <div className="card p-8 text-center relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: isNeon ? .06 : .04 }}>
        <Laurel size={220} color={isNeon ? ARENA.gold : 'var(--s-700)'}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
      <div className="relative">
        {emoji ? <div className="text-4xl mb-2">{emoji}</div>
          : Icon ? <Icon size={36} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          : null}
        <p className="font-display font-bold" style={{ color: 'var(--text-soft)' }}>{title}</p>
        {sub && <p className="text-sm mt-1 max-w-xs mx-auto" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

/* ============================================================
   VERSUS EMBLEM
   ============================================================ */
export function VersusEmblem({ active, color, size = 44 }) {
  const { isNeon } = useTheme();

  return (
    <motion.div className="relative grid place-items-center rounded-full font-display font-black"
      style={{
        width: size, height: size,
        background: active ? (color || ARENA.goldFrame) : 'rgba(128,128,128,.12)',
        border: `2px solid ${active ? (color || ARENA.gold) : 'var(--border-strong)'}`,
        color: active ? (isNeon ? '#04121a' : '#fff') : 'var(--text-muted)',
        boxShadow: active ? `0 0 22px -2px ${color || ARENA.gold}` : 'none',
        fontSize: size * 0.32,
      }}
      animate={active ? { rotate: [0, 6, -6, 0], scale: [1, 1.06, 1] } : {}}
      transition={{ duration: 2.2, repeat: Infinity }}>
      VS
    </motion.div>
  );
}

/* ============================================================
   PROGRESS RING
   ============================================================ */
export function ProgressRing({ value, max = 100, size = 56, stroke = 5, color, label, icon }) {
  const { isNeon } = useTheme();
  const pct = Math.max(0, Math.min(1, value / max));
  const rad = (size - stroke) / 2;
  const circ = 2 * Math.PI * rad;
  const c = color || ARENA.gold;

  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={rad}
          fill="none" stroke="var(--s-200)" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={rad}
          fill="none" stroke={c} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ}
          style={isNeon ? { filter: `drop-shadow(0 0 5px ${c})` } : {}}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ * (1 - pct) }}
          transition={{ duration: .8, ease: 'easeOut' }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        {icon ? <span style={{ fontSize: size * 0.3 }}>{icon}</span>
          : <span className="font-display font-black" style={{ fontSize: size * 0.24 }}>{label}</span>}
      </div>
    </div>
  );
}

/* ============================================================
   ARENA FLOOR
   ============================================================ */
export function ArenaFloor({ children, classKey, className = '', showCrowd = true }) {
  const { isNeon } = useTheme();
  const t = classKey ? themeOf(classKey) : null;

  return (
    <div className={`relative overflow-hidden ${className}`}
      style={{ background: t?.arenaBg || ARENA.arenaDark }}>

      {t && (
        <motion.div className="absolute inset-0"
          style={{ background: t.radial, opacity: isNeon ? .5 : .4 }}
          animate={{ opacity: isNeon ? [.32, .58, .32] : [.28, .48, .28] }}
          transition={{ duration: 5.5, repeat: Infinity }} />
      )}

      {showCrowd && <CrowdRow className="top-0" opacity={isNeon ? .4 : .3} />}

      {/* arches (roman) / grid (neon) */}
      {isNeon ? (
        <div className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,229,255,.055) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,229,255,.055) 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%, black, transparent)',
          }} />
      ) : (
        <div className="absolute inset-x-0 top-4 h-12 opacity-[.12] pointer-events-none"
          style={{
            backgroundImage: `
              radial-gradient(circle at 12.5% 100%, transparent 22px, #000 23px, #000 26px, transparent 27px),
              radial-gradient(circle at 37.5% 100%, transparent 22px, #000 23px, #000 26px, transparent 27px),
              radial-gradient(circle at 62.5% 100%, transparent 22px, #000 23px, #000 26px, transparent 27px),
              radial-gradient(circle at 87.5% 100%, transparent 22px, #000 23px, #000 26px, transparent 27px)`,
            backgroundSize: '25% 100%',
          }} />
      )}

      {/* floor */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
        style={{
          background: isNeon
            ? 'linear-gradient(180deg, transparent, rgba(0,229,255,.10))'
            : 'linear-gradient(180deg, transparent, rgba(122,103,73,.42))',
        }} />

      <DustLayer count={isNeon ? 18 : 14} />
      <Torch className="left-4 top-8" size="sm" />
      <Torch className="right-4 top-8" size="sm" delay={0.8} />

      <div className="relative">{children}</div>
    </div>
  );
}

export { ARENA };